import React, { useEffect, useState, useRef, useCallback } from 'react';
import baseURL from '../../url';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSync,
    faClock,
    faQrcode,
    faUserTie,
    faCashRegister,
    faVolumeUp,
    faExclamationTriangle,
    faEye
} from '@fortawesome/free-solid-svg-icons';
import { playSound } from '../../../utils/soundService';
import { getConfig } from '../../../utils/configService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './KDS.css';

export default function KDS() {
    const [pedidos, setPedidos] = useState([]);
    const [mesas, setMesas] = useState({});
    const [lastUpdated, setLastUpdated] = useState(new Date());
    const [pedidoSeleccionado, setPedidoSeleccionado] = useState(null);
    const [filtroEstado, setFiltroEstado] = useState('activos'); // 'activos', 'Pendiente', 'En preparación', 'Listo', 'todos'
    const [alertaReciente, setAlertaReciente] = useState(null);

    const prevOrderIdsRef = useRef(new Set());
    const isFirstLoadRef = useRef(true);

    const cargarMesas = useCallback(async () => {
        try {
            const res = await fetch(`${baseURL}/mesaGet.php`);
            const data = await res.json();
            if (data.mesas) {
                const mesaMap = {};
                data.mesas.forEach(m => {
                    mesaMap[m.idMesa] = m.mesa;
                });
                setMesas(mesaMap);
            }
        } catch (e) {
            console.error('Error al cargar mesas en KDS:', e);
        }
    }, []);

    const cargarPedidos = useCallback(async (isSilent = false) => {
        try {
            const res = await fetch(`${baseURL}/pedidoGet.php`);
            const data = await res.json();
            const fetched = data.pedidos || [];

            // Detectar nuevos pedidos para reproducir sonido de alerta
            if (!isFirstLoadRef.current) {
                const nuevos = fetched.filter(p => !prevOrderIdsRef.current.has(p.idPedido) && p.estado !== 'Pagado' && p.estado !== 'Rechazado' && p.estado !== 'Entregado');
                if (nuevos.length > 0) {
                    const nuevo = nuevos[0];
                    const cfg = getConfig();
                    const soundKey = nuevo.origen === 'qr' ? cfg.sonidoPedidoQR : cfg.sonidoPedidoMesero;
                    playSound(soundKey);

                    setAlertaReciente({
                        idPedido: nuevo.idPedido,
                        mesa: mesas[nuevo.idMesa] || `Mesa #${nuevo.idMesa || '?'}` || 'Para llevar',
                        origen: nuevo.origen || 'qr',
                        hora: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                        total: nuevo.total
                    });

                    toast.info(`🔔 ¡Nuevo pedido recibido! ${mesas[nuevo.idMesa] || 'Mesa'}`, {
                        position: 'top-right',
                        autoClose: 5000
                    });
                }
            } else {
                isFirstLoadRef.current = false;
            }

            // Actualizar el set de IDs conocidos
            const currentIds = new Set(fetched.map(p => p.idPedido));
            prevOrderIdsRef.current = currentIds;

            setPedidos(fetched.reverse());
            setLastUpdated(new Date());
            
        } catch (e) {
            console.error('Error al cargar pedidos en KDS:', e);
            
        }
    }, [mesas]);

    useEffect(() => {
        cargarMesas().then(() => {
            cargarPedidos();
        });

        const interval = setInterval(() => {
            cargarPedidos(true);
        }, 6000);

        return () => clearInterval(interval);
    }, [cargarMesas, cargarPedidos]);

    const cambiarEstado = async (idPedido, nuevoEstado) => {
        try {
            const res = await fetch(`${baseURL}/pedidoPut.php?idPedido=${idPedido}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ estado: nuevoEstado })
            });
            const data = await res.json();
            if (data.mensaje) {
                toast.success(`Pedido #${idPedido} actualizado a "${nuevoEstado}"`);
                setPedidos(prev => prev.map(p => p.idPedido === idPedido ? { ...p, estado: nuevoEstado } : p));
                if (pedidoSeleccionado && pedidoSeleccionado.idPedido === idPedido) {
                    setPedidoSeleccionado(prev => ({ ...prev, estado: nuevoEstado }));
                }
            } else {
                toast.error(data.error || 'Error al actualizar pedido');
            }
        } catch (e) {
            console.error(e);
            toast.error('Error de red al actualizar estado');
        }
    };

    const getOrigenBadge = (origen) => {
        switch (origen) {
            case 'qr':
                return <span className="kds-badge badge-qr"><FontAwesomeIcon icon={faQrcode} /> QR Mesa</span>;
            case 'mesero':
                return <span className="kds-badge badge-mesero"><FontAwesomeIcon icon={faUserTie} /> Mesero</span>;
            case 'pos':
                return <span className="kds-badge badge-pos"><FontAwesomeIcon icon={faCashRegister} /> POS Caja</span>;
            default:
                return <span className="kds-badge badge-default">Tienda</span>;
        }
    };

    const parseProductos = (productosRaw) => {
        if (!productosRaw) return [];
        if (typeof productosRaw === 'object' && Array.isArray(productosRaw)) return productosRaw;
        try {
            return JSON.parse(productosRaw);
        } catch (e) {
            return [];
        }
    };

    const formatearTiempoTranscurrido = (createdAt) => {
        if (!createdAt) return '';
        const createdDate = new Date(createdAt.replace(' ', 'T'));
        if (isNaN(createdDate.getTime())) return createdAt;
        const diffMin = Math.floor((new Date() - createdDate) / 60000);
        if (diffMin < 1) return 'Hace instantes';
        if (diffMin === 1) return 'Hace 1 min';
        if (diffMin < 60) return `Hace ${diffMin} min`;
        const diffHrs = Math.floor(diffMin / 60);
        return `Hace ${diffHrs}h ${diffMin % 60}m`;
    };

    // Filtrar pedidos según el estado
    const pedidosFiltrados = pedidos.filter(p => {
        if (filtroEstado === 'activos') {
            return p.estado === 'Pendiente' || p.estado === 'En preparación' || p.estado === 'Listo';
        }
        if (filtroEstado === 'todos') return true;
        return p.estado === filtroEstado;
    });

    const pedidosPendientes = pedidos.filter(p => p.estado === 'Pendiente');
    const pedidosPreparando = pedidos.filter(p => p.estado === 'En preparación');
    const pedidosListos = pedidos.filter(p => p.estado === 'Listo');

    return (
        <div className="kds-wrapper">
            <ToastContainer />

            {/* Header del KDS */}
            <div className="kds-topbar">
                <div className="kds-title-area">
                    <h2>🍳 Panel de Cocina (KDS)</h2>
                    <span className="kds-clock">
                        <FontAwesomeIcon icon={faClock} /> Actualizado: {lastUpdated.toLocaleTimeString()}
                    </span>
                </div>

                <div className="kds-actions-area">
                    <div className="kds-filter-tabs">
                        <button
                            className={`kds-tab ${filtroEstado === 'activos' ? 'active' : ''}`}
                            onClick={() => setFiltroEstado('activos')}
                        >
                            Vista Kanban ({pedidosPendientes.length + pedidosPreparando.length + pedidosListos.length})
                        </button>
                        <button
                            className={`kds-tab ${filtroEstado === 'Pendiente' ? 'active' : ''}`}
                            onClick={() => setFiltroEstado('Pendiente')}
                        >
                            Pendientes ({pedidosPendientes.length})
                        </button>
                        <button
                            className={`kds-tab ${filtroEstado === 'En preparación' ? 'active' : ''}`}
                            onClick={() => setFiltroEstado('En preparación')}
                        >
                            En Cocina ({pedidosPreparando.length})
                        </button>
                        <button
                            className={`kds-tab ${filtroEstado === 'Listo' ? 'active' : ''}`}
                            onClick={() => setFiltroEstado('Listo')}
                        >
                            Listos ({pedidosListos.length})
                        </button>
                    </div>

                    <button className="kds-reload-btn" onClick={() => cargarPedidos()} title="Recargar">
                        <FontAwesomeIcon icon={faSync} />
                    </button>
                </div>
            </div>

            {/* Banner de alerta interactiva al hacer clic */}
            {alertaReciente && (
                <div
                    className="kds-alert-banner"
                    onClick={() => {
                        const target = pedidos.find(p => p.idPedido === alertaReciente.idPedido);
                        if (target) setPedidoSeleccionado(target);
                    }}
                >
                    <div className="alert-banner-left">
                        <FontAwesomeIcon icon={faVolumeUp} className="alert-bell-anim" />
                        <div>
                            <strong>¡Nuevo Pedido #{alertaReciente.idPedido}!</strong> {alertaReciente.mesa} ({alertaReciente.hora})
                        </div>
                    </div>
                    <span className="alert-banner-cta">Toca para abrir detalle &rarr;</span>
                </div>
            )}

            {/* Vista Tablero Kanban si está en 'activos' */}
            {filtroEstado === 'activos' ? (
                <div className="kds-kanban-board">
                    {/* Columna Pendientes */}
                    <div className="kanban-col col-pendiente">
                        <div className="kanban-col-header">
                            <span className="col-indicator indicator-yellow"></span>
                            <h3>Pendientes</h3>
                            <span className="col-badge">{pedidosPendientes.length}</span>
                        </div>
                        <div className="kanban-col-cards">
                            {pedidosPendientes.length === 0 ? (
                                <p className="kds-empty-col">No hay pedidos pendientes</p>
                            ) : (
                                pedidosPendientes.map(pedido => (
                                    <KDSCard
                                        key={pedido.idPedido}
                                        pedido={pedido}
                                        mesaNombre={mesas[pedido.idMesa] || (pedido.idMesa ? `Mesa #${pedido.idMesa}` : 'Para llevar')}
                                        getOrigenBadge={getOrigenBadge}
                                        parseProductos={parseProductos}
                                        formatearTiempoTranscurrido={formatearTiempoTranscurrido}
                                        onVerDetalle={() => setPedidoSeleccionado(pedido)}
                                        onAvanzar={() => cambiarEstado(pedido.idPedido, 'En preparación')}
                                        avanzarTexto="🍳 Preparar"
                                    />
                                ))
                            )}
                        </div>
                    </div>

                    {/* Columna En preparación */}
                    <div className="kanban-col col-preparando">
                        <div className="kanban-col-header">
                            <span className="col-indicator indicator-blue"></span>
                            <h3>En Preparación</h3>
                            <span className="col-badge">{pedidosPreparando.length}</span>
                        </div>
                        <div className="kanban-col-cards">
                            {pedidosPreparando.length === 0 ? (
                                <p className="kds-empty-col">No hay pedidos en preparación</p>
                            ) : (
                                pedidosPreparando.map(pedido => (
                                    <KDSCard
                                        key={pedido.idPedido}
                                        pedido={pedido}
                                        mesaNombre={mesas[pedido.idMesa] || (pedido.idMesa ? `Mesa #${pedido.idMesa}` : 'Para llevar')}
                                        getOrigenBadge={getOrigenBadge}
                                        parseProductos={parseProductos}
                                        formatearTiempoTranscurrido={formatearTiempoTranscurrido}
                                        onVerDetalle={() => setPedidoSeleccionado(pedido)}
                                        onAvanzar={() => cambiarEstado(pedido.idPedido, 'Listo')}
                                        avanzarTexto="✅ Listo"
                                    />
                                ))
                            )}
                        </div>
                    </div>

                    {/* Columna Listos */}
                    <div className="kanban-col col-listo">
                        <div className="kanban-col-header">
                            <span className="col-indicator indicator-green"></span>
                            <h3>Listos para Entrega</h3>
                            <span className="col-badge">{pedidosListos.length}</span>
                        </div>
                        <div className="kanban-col-cards">
                            {pedidosListos.length === 0 ? (
                                <p className="kds-empty-col">No hay pedidos listos</p>
                            ) : (
                                pedidosListos.map(pedido => (
                                    <KDSCard
                                        key={pedido.idPedido}
                                        pedido={pedido}
                                        mesaNombre={mesas[pedido.idMesa] || (pedido.idMesa ? `Mesa #${pedido.idMesa}` : 'Para llevar')}
                                        getOrigenBadge={getOrigenBadge}
                                        parseProductos={parseProductos}
                                        formatearTiempoTranscurrido={formatearTiempoTranscurrido}
                                        onVerDetalle={() => setPedidoSeleccionado(pedido)}
                                        onAvanzar={() => cambiarEstado(pedido.idPedido, 'Entregado')}
                                        avanzarTexto="🚀 Entregar"
                                    />
                                ))
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                /* Vista Lista filtrada */
                <div className="kds-grid-view">
                    {pedidosFiltrados.length === 0 ? (
                        <div className="kds-no-data">No hay pedidos con el estado seleccionado</div>
                    ) : (
                        pedidosFiltrados.map(pedido => (
                            <KDSCard
                                key={pedido.idPedido}
                                pedido={pedido}
                                mesaNombre={mesas[pedido.idMesa] || (pedido.idMesa ? `Mesa #${pedido.idMesa}` : 'Para llevar')}
                                getOrigenBadge={getOrigenBadge}
                                parseProductos={parseProductos}
                                formatearTiempoTranscurrido={formatearTiempoTranscurrido}
                                onVerDetalle={() => setPedidoSeleccionado(pedido)}
                                onAvanzar={() => {
                                    if (pedido.estado === 'Pendiente') cambiarEstado(pedido.idPedido, 'En preparación');
                                    else if (pedido.estado === 'En preparación') cambiarEstado(pedido.idPedido, 'Listo');
                                    else if (pedido.estado === 'Listo') cambiarEstado(pedido.idPedido, 'Entregado');
                                }}
                                avanzarTexto={
                                    pedido.estado === 'Pendiente' ? '🍳 Preparar' :
                                    pedido.estado === 'En preparación' ? '✅ Listo' :
                                    pedido.estado === 'Listo' ? '🚀 Entregar' : null
                                }
                            />
                        ))
                    )}
                </div>
            )}

            {/* Modal de Detalle de Pedido */}
            {pedidoSeleccionado && (
                <div className="modal modal-kds-overlay">
                    <div className="modal-content modal-kds-content">
                        <div className="deFlexBtnsModal">
                            <div className="kds-modal-header-info">
                                <h3>Pedido #{pedidoSeleccionado.idPedido}</h3>
                                {getOrigenBadge(pedidoSeleccionado.origen)}
                            </div>
                            <span className="close" onClick={() => setPedidoSeleccionado(null)}>&times;</span>
                        </div>

                        <div className="kds-modal-body">
                            <div className="kds-modal-meta-grid">
                                <div>
                                    <span className="meta-label">Mesa / Destino:</span>
                                    <strong>{mesas[pedidoSeleccionado.idMesa] || 'Para llevar'}</strong>
                                </div>
                                <div>
                                    <span className="meta-label">Cliente:</span>
                                    <strong>{pedidoSeleccionado.nombre || 'No especificado'}</strong>
                                </div>
                                <div>
                                    <span className="meta-label">Hora:</span>
                                    <span>{pedidoSeleccionado.createdAt}</span>
                                </div>
                                <div>
                                    <span className="meta-label">Estado Actual:</span>
                                    <span className={`kds-status-pill status-${pedidoSeleccionado.estado?.toLowerCase()}`}>
                                        {pedidoSeleccionado.estado}
                                    </span>
                                </div>
                            </div>

                            {pedidoSeleccionado.nota && (
                                <div className="kds-modal-note-box">
                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                    <span><strong>Nota especial:</strong> {pedidoSeleccionado.nota}</span>
                                </div>
                            )}

                            <h4>Productos a Preparar:</h4>
                            <div className="kds-modal-products-list">
                                {parseProductos(pedidoSeleccionado.productos).map((prod, idx) => (
                                    <div key={idx} className="kds-modal-prod-item">
                                        <div className="prod-cant-box">x{prod.cantidad}</div>
                                        <div className="prod-details">
                                            <strong>{prod.titulo}</strong>
                                            {prod.item && <span className="prod-item-sub">{prod.item}</span>}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="kds-modal-footer-actions">
                                <button
                                    className="btn-status-action btn-pending"
                                    onClick={() => cambiarEstado(pedidoSeleccionado.idPedido, 'Pendiente')}
                                >
                                    Marcar Pendiente
                                </button>
                                <button
                                    className="btn-status-action btn-prep"
                                    onClick={() => cambiarEstado(pedidoSeleccionado.idPedido, 'En preparación')}
                                >
                                    🍳 En Preparación
                                </button>
                                <button
                                    className="btn-status-action btn-ready"
                                    onClick={() => cambiarEstado(pedidoSeleccionado.idPedido, 'Listo')}
                                >
                                    ✅ Listo para Entrega
                                </button>
                                <button
                                    className="btn-status-action btn-delivered"
                                    onClick={() => cambiarEstado(pedidoSeleccionado.idPedido, 'Entregado')}
                                >
                                    🚀 Entregado
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Subcomponente de Tarjeta KDS
function KDSCard({
    pedido,
    mesaNombre,
    getOrigenBadge,
    parseProductos,
    formatearTiempoTranscurrido,
    onVerDetalle,
    onAvanzar,
    avanzarTexto
}) {
    const productos = parseProductos(pedido.productos);
    const tiempo = formatearTiempoTranscurrido(pedido.createdAt);

    return (
        <div className={`kds-card state-${pedido.estado?.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="kds-card-header">
                <div className="kds-card-title">
                    <span className="kds-order-num">#{pedido.idPedido}</span>
                    <span className="kds-mesa-title">{mesaNombre}</span>
                </div>
                {getOrigenBadge(pedido.origen)}
            </div>

            <div className="kds-card-timer">
                <FontAwesomeIcon icon={faClock} /> {tiempo}
            </div>

            {pedido.nota && (
                <div className="kds-card-alert-note">
                    ⚠️ {pedido.nota}
                </div>
            )}

            <div className="kds-card-items">
                {productos.map((prod, idx) => (
                    <div key={idx} className="kds-item-line">
                        <span className="kds-item-qty">x{prod.cantidad}</span>
                        <div className="kds-item-info">
                            <span className="kds-item-name">{prod.titulo}</span>
                            {prod.item && <span className="kds-item-detail">{prod.item}</span>}
                        </div>
                    </div>
                ))}
            </div>

            <div className="kds-card-actions">
                <button className="kds-card-btn-view" onClick={onVerDetalle} title="Ver detalle completo">
                    <FontAwesomeIcon icon={faEye} /> Detalle
                </button>
                {avanzarTexto && (
                    <button className="kds-card-btn-advance" onClick={onAvanzar}>
                        {avanzarTexto}
                    </button>
                )}
            </div>
        </div>
    );
}
